/**
 * AdminPartnerSection — componente stand-alone per la sezione
 * "Aziende Partner" nel pannello admin.
 * Contiene:
 *  - Tabella aziende partner + CRUD
 *  - Tabella utenti partner con assegnazione azienda
 *  - Dettaglio azienda: stats, sales users, preventivi
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/apiMode';
import { Building2, Users, Plus, Edit2, Trash2, ChevronLeft, BarChart2, FileText } from 'lucide-react';

const tdS = { padding: '0.7rem 0.9rem', borderBottom: '1px solid #f1f5f9', fontSize: '0.865rem', verticalAlign: 'middle' };
const thS = { ...tdS, background: '#f8fafc', fontWeight: 700, fontSize: '0.73rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' };

export default function AdminPartnerSection({ token }) {
  const api = useMemo(() => axios.create({
    baseURL: API_BASE_URL,
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  // ── state ────────────────────────────────────────────────
  const [view, setView] = useState('list'); // 'list' | 'detail'
  const [companies, setCompanies] = useState([]);
  const [partnerUsers, setPartnerUsers] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]); // for dropdown
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [activeTab, setActiveTab] = useState('companies'); // 'companies' | 'users'

  // Company form
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({ name: '', country: '', start_date: '' });
  const [savingCompany, setSavingCompany] = useState(false);

  // ── load ─────────────────────────────────────────────────
  const loadAll = async () => {
    try {
      setLoading(true);
      const [c, u] = await Promise.all([
        api.get('/api/partner/admin/companies'),
        api.get('/api/partner/admin/partner-users')
      ]);
      setCompanies(c.data || []);
      setAllCompanies(c.data || []);
      setPartnerUsers(u.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Errore caricamento dati partner');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) loadAll(); }, [token]);

  const fmt = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';
  const fmtCurrency = (n) => n ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(n) : '—';

  // ── Company CRUD ─────────────────────────────────────────
  const openAddCompany = () => {
    setEditingCompany(null);
    setCompanyForm({ name: '', country: '', start_date: '' });
    setShowCompanyForm(true);
  };

  const openEditCompany = (c) => {
    setEditingCompany(c);
    setCompanyForm({ name: c.name, country: c.country || '', start_date: c.start_date ? c.start_date.slice(0, 10) : '' });
    setShowCompanyForm(true);
  };

  const saveCompany = async () => {
    if (!companyForm.name.trim()) return;
    setSavingCompany(true);
    try {
      if (editingCompany) {
        await api.put(`/api/partner/admin/companies/${editingCompany.id}`, companyForm);
      } else {
        await api.post('/api/partner/admin/companies', companyForm);
      }
      setShowCompanyForm(false);
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Errore salvataggio');
    } finally {
      setSavingCompany(false);
    }
  };

  const deleteCompany = async (c) => {
    if (!window.confirm(`Eliminare l'azienda "${c.name}"? Gli utenti partner rimarranno ma non avranno più un'azienda associata.`)) return;
    try {
      await api.delete(`/api/partner/admin/companies/${c.id}`);
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Errore eliminazione');
    }
  };

  // ── Partner user → company assignment ──────────────────
  const assignCompany = async (userId, companyId) => {
    try {
      await api.patch(`/api/partner/admin/users/${userId}/company`, { partner_company_id: companyId || null });
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Errore assegnazione');
    }
  };

  // ── Company detail ────────────────────────────────────────
  const openDetail = async (company) => {
    setLoadingDetail(true);
    setView('detail');
    setDetail(null);
    try {
      const r = await api.get(`/api/partner/admin/companies/${company.id}`);
      setDetail(r.data);
    } catch (err) {
      setDetail({ error: err.response?.data?.error || 'Errore caricamento dettaglio' });
    } finally {
      setLoadingDetail(false);
    }
  };

  // ── Detail view ───────────────────────────────────────────
  if (view === 'detail') {
    return (
      <div>
        <button
          onClick={() => { setView('list'); setDetail(null); }}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.45rem 0.75rem', cursor: 'pointer', fontWeight: 600, color: '#334155', marginBottom: '1.25rem', fontSize: '0.85rem' }}
        >
          <ChevronLeft size={15} /> Torna alle Aziende Partner
        </button>

        {loadingDetail ? (
          <div style={{ color: '#64748b' }}>Caricamento...</div>
        ) : detail?.error ? (
          <div style={{ color: '#b91c1c' }}>{detail.error}</div>
        ) : detail ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Header */}
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <Building2 size={22} color="#1d4ed8" />
                    <h2 style={{ margin: 0, color: '#1e3a8a', fontSize: '1.25rem', fontWeight: 800 }}>{detail.company.name}</h2>
                  </div>
                  <div style={{ marginTop: '0.35rem', color: '#64748b', fontSize: '0.85rem' }}>
                    {detail.company.country && <span>🌍 {detail.company.country}</span>}
                    {detail.company.start_date && <span style={{ marginLeft: '1rem' }}>📅 Dal {fmt(detail.company.start_date)}</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
              {[
                { label: 'Utenti Sales', value: detail.stats.total_partners, Icon: Users, color: '#1d4ed8' },
                { label: 'Preventivi Totali', value: detail.stats.total_capex, Icon: FileText, color: '#7c3aed' },
                { label: 'Valore Totale', value: fmtCurrency(detail.stats.total_value), Icon: BarChart2, color: '#0f766e' },
              ].map(({ label, value, Icon, color }) => (
                <div key={label} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={20} color={color} />
                  </div>
                  <div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a' }}>{value}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Partner users */}
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', fontWeight: 700, color: '#1e3a8a', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Users size={16} /> Utenti Sales Associati ({detail.partners.length})
              </div>
              {detail.partners.length === 0 ? (
                <div style={{ padding: '1.25rem', color: '#64748b', fontSize: '0.87rem' }}>Nessun utente sales associato.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>{['Email', 'Nome', 'Registrato il'].map(h => <th key={h} style={thS}>{h}</th>)}</tr></thead>
                  <tbody>
                    {detail.partners.map(u => (
                      <tr key={u.id}>
                        <td style={tdS}>{u.email}</td>
                        <td style={tdS}>{u.full_name || '—'}</td>
                        <td style={tdS}>{fmt(u.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Preventivi */}
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', fontWeight: 700, color: '#1e3a8a', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FileText size={16} /> Preventivi ({detail.capex.length})
              </div>
              {detail.capex.length === 0 ? (
                <div style={{ padding: '1.25rem', color: '#64748b', fontSize: '0.87rem' }}>Nessun preventivo.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>{['Sales User', 'Cliente', 'Nome Preventivo', 'Valore', 'Data'].map(h => <th key={h} style={thS}>{h}</th>)}</tr></thead>
                  <tbody>
                    {detail.capex.map(c => (
                      <tr key={c.id}>
                        <td style={tdS}>{c.full_name || c.email}</td>
                        <td style={tdS}>{c.customer_name ? `${c.customer_name}${c.customer_company ? ` — ${c.customer_company}` : ''}` : '—'}</td>
                        <td style={tdS}>{c.name || '—'}</td>
                        <td style={tdS}>{c.total_capex ? `${parseInt(c.total_capex).toLocaleString('it-IT')} €` : '—'}</td>
                        <td style={tdS}>{fmt(c.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────
  return (
    <div>
      {/* Company form modal — inlined to avoid focus loss on keystroke */}
      {showCompanyForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ margin: '0 0 1.25rem', color: '#1e3a8a', fontSize: '1.05rem' }}>
              {editingCompany ? 'Modifica Azienda Partner' : 'Nuova Azienda Partner'}
            </h2>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.3rem' }}>
                Nome Azienda <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={companyForm.name}
                onChange={e => setCompanyForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Acme Srl"
                style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.3rem' }}>
                Nazione
              </label>
              <input
                type="text"
                value={companyForm.country}
                onChange={e => setCompanyForm(f => ({ ...f, country: e.target.value }))}
                placeholder="Italia"
                style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.3rem' }}>
                Data Inizio Collaborazione
              </label>
              <input
                type="date"
                value={companyForm.start_date}
                onChange={e => setCompanyForm(f => ({ ...f, start_date: e.target.value }))}
                style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
              <button onClick={() => setShowCompanyForm(false)} style={{ padding: '0.55rem 1rem', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#334155', fontWeight: 600, cursor: 'pointer' }}>
                Annulla
              </button>
              <button
                onClick={saveCompany}
                disabled={savingCompany || !companyForm.name.trim()}
                style={{ padding: '0.55rem 1.25rem', borderRadius: 8, border: 'none', background: '#1d4ed8', color: 'white', fontWeight: 700, cursor: 'pointer' }}
              >
                {savingCompany ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e3a8a', fontWeight: 800 }}>Aziende Partner</h2>
          <p style={{ margin: '0.25rem 0 0', color: '#64748b', fontSize: '0.87rem' }}>Gestisci le aziende partner e i loro utenti sales.</p>
        </div>
        <button
          onClick={openAddCompany}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#1d4ed8', color: 'white', border: 'none', borderRadius: 8, padding: '0.55rem 1rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.87rem' }}
        >
          <Plus size={15} /> Nuova Azienda
        </button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.75rem', borderRadius: 8, marginBottom: '1rem', border: '1px solid #fecaca' }}>{error}</div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '1rem' }}>
        {[
          { key: 'companies', label: `🏢 Aziende (${companies.length})` },
          { key: 'users', label: `👤 Utenti Partner (${partnerUsers.length})` }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{ padding: '0.4rem 0.9rem', borderRadius: 999, border: '1px solid #cbd5e1', background: activeTab === tab.key ? '#1d4ed8' : 'white', color: activeTab === tab.key ? 'white' : '#334155', fontWeight: 700, cursor: 'pointer', fontSize: '0.83rem' }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: '#64748b' }}>Caricamento...</div>
      ) : activeTab === 'companies' ? (
        /* ── Companies table ── */
        companies.length === 0 ? (
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '2.5rem', textAlign: 'center', color: '#64748b' }}>
            <Building2 size={40} style={{ opacity: 0.3, marginBottom: '1rem' }} />
            <p>Nessuna azienda partner. Clicca "Nuova Azienda" per iniziare.</p>
          </div>
        ) : (
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>{['Nome', 'Nazione', 'Inizio Collaborazione', 'Utenti Sales', 'Azioni'].map(h => <th key={h} style={thS}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {companies.map(c => (
                  <tr key={c.id}>
                    <td style={{ ...tdS, fontWeight: 700, color: '#0f172a' }}>{c.name}</td>
                    <td style={tdS}>{c.country || '—'}</td>
                    <td style={tdS}>{fmt(c.start_date)}</td>
                    <td style={tdS}>
                      <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '0.2rem 0.55rem', borderRadius: 999, fontWeight: 700, fontSize: '0.78rem' }}>
                        {c.partner_count}
                      </span>
                    </td>
                    <td style={tdS}>
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button onClick={() => openDetail(c)} style={{ border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', borderRadius: 7, padding: '0.3rem 0.5rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}>
                          Dettagli
                        </button>
                        <button onClick={() => openEditCompany(c)} style={{ border: '1px solid #e2e8f0', background: '#f8fafc', color: '#334155', borderRadius: 7, padding: '0.3rem 0.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.78rem' }}>
                          <Edit2 size={12} />
                        </button>
                        <button onClick={() => deleteCompany(c)} style={{ border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', borderRadius: 7, padding: '0.3rem 0.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.78rem' }}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        /* ── Partner users table ── */
        partnerUsers.length === 0 ? (
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '2.5rem', textAlign: 'center', color: '#64748b' }}>
            <Users size={40} style={{ opacity: 0.3, marginBottom: '1rem' }} />
            <p>Nessun utente con ruolo "partner". Assegna il ruolo da Gestione Utenti.</p>
          </div>
        ) : (
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>{['Email', 'Nome', 'Azienda Partner', 'Registrato il'].map(h => <th key={h} style={thS}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {partnerUsers.map(u => (
                  <tr key={u.id}>
                    <td style={tdS}>{u.email}</td>
                    <td style={tdS}>{u.full_name || '—'}</td>
                    <td style={tdS}>
                      <select
                        value={u.partner_company_id || ''}
                        onChange={e => assignCompany(u.id, e.target.value || null)}
                        style={{ padding: '0.3rem 0.5rem', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: '0.83rem', minWidth: 160 }}
                      >
                        <option value="">— Nessuna azienda —</option>
                        {allCompanies.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </td>
                    <td style={tdS}>{fmt(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
